import {
  Href,
  NativeStackHeaderProps,
  router,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import useDebtSaleAccountDetails, {
  DebtSaleAccountDetailsStatus,
} from 'shared/components/DebtSale/hooks/useDebtSaleAccountDetails';
import ProductDebtSoldContent from 'shared/components/DebtSale/ProductDebtSoldScreen/ProductDebtSoldContent';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const PRODUCT_DEBT_SOLD_ERROR_ANALYTICS_KEY =
  'debt-sale-product-debt-sold-error';

export enum DebtSoldProduct {
  Advance = 'ADVANCE',
  LineOfCredit = 'LINE_OF_CREDIT',
}

const HEADER_TITLE_KEY: Record<DebtSoldProduct, string> = {
  [DebtSoldProduct.Advance]: 'headerTitle.advance',
  [DebtSoldProduct.LineOfCredit]: 'headerTitle.lineOfCredit',
};

interface ProductDebtSoldSettings {
  pathname: Href;
  analyticsId: string;
}

interface ProductDebtSoldScreenProps {
  product: DebtSoldProduct;
  settings?: ProductDebtSoldSettings;
}

const ProductDebtSoldScreen = ({
  product,
  settings,
}: ProductDebtSoldScreenProps) => {
  const { accountId: accountIdParam } = useLocalSearchParams<{
    accountId?: string;
  }>();

  const accountId =
    typeof accountIdParam === 'string' ? accountIdParam.trim() : '';
  const hasValidAccountId = accountId.length > 0;

  const { t: translatedText } = useTranslation('debtSale', {
    keyPrefix: 'productDebtSoldScreen',
  });

  const { defaultTitle, defaultMessage, retryButtonTitle } =
    useDefaultErrorStrings();

  const { result, fetching, error, refetch } =
    useDebtSaleAccountDetails(accountId);

  const hasError = !hasValidAccountId || !!error;

  const typedError =
    result?.status === DebtSaleAccountDetailsStatus.Error
      ? result.typedError
      : null;

  const successResult =
    hasValidAccountId && result?.status === DebtSaleAccountDetailsStatus.Success
      ? result.success
      : null;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText(HEADER_TITLE_KEY[product]),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              trailingAccessories={
                settings ? (
                  <IconButton
                    iconKey="SETTINGS"
                    iconSize="MD"
                    iconButtonAccessibilityLabel="SETTINGS"
                    iconOnPress={() => {
                      router.navigate(settings.pathname);
                    }}
                    analyticsId={settings.analyticsId}
                  />
                ) : null
              }
              {...props}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={typedError ? typedError.title : defaultTitle}
              messageBody={typedError ? typedError.message : defaultMessage}
              primaryButton={{
                title: retryButtonTitle,
                onPress: refetch,
              }}
              screenAnalyticsEventKey={PRODUCT_DEBT_SOLD_ERROR_ANALYTICS_KEY}
              error={error}
            />
          ) : null
        }
      />
      {successResult ? (
        <ProductDebtSoldContent success={successResult} />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default ProductDebtSoldScreen;
